import { apiDelete, apiGet, apiPost, apiPut } from './api-client'
import type {
  AssistantMessage,
  CalendarConnection,
  CalendarProvider,
  Destination,
  EventCandidate,
  FamilyMember,
  IngestChannel,
  LinkedCalendar,
  ParsedEventDraft,
  PlannerEvent,
  ScanRule,
  TravelDeal,
} from '../types/family-planner'
import { MEMBERS } from './family-planner-data'
import { answerLocally, parseEventText, type AssistantContext } from './family-planner-analytics'
import {
  completeMicrosoftRedirect,
  connectGoogle,
  connectMicrosoft,
  getSession,
  isProviderConfigured,
  listSessions,
  revokeProvider,
  type OAuthProvider,
  type OAuthSession,
} from './planner-oauth'
import { fetchProviderCalendars, fetchProviderEvents } from './planner-providers'

const PLANNER_API_URL = import.meta.env.VITE_PLANNER_API_URL || ''
const HAS_API = !!PLANNER_API_URL

/**
 * Three ways this module can be running:
 *   backend — the deployed planner service is answering (scheduled sync, Gmail
 *             and WhatsApp ingest, travel pricing).
 *   direct  — no backend, but a Google or Microsoft account is linked straight
 *             from the browser, so calendars are real and sync on demand.
 *   local   — nothing linked; only what the owner typed here.
 */
export type PlannerMode = 'backend' | 'direct' | 'local'

export function getPlannerMode(): PlannerMode {
  if (HAS_API && !_apiUnreachable) return 'backend'
  return listSessions().length > 0 ? 'direct' : 'local'
}

/** True when no deployed backend is answering, whatever else is linked. */
export function isPlannerOffline(): boolean {
  return !HAS_API || _apiUnreachable
}

let _apiUnreachable = false

/** Operations that genuinely cannot work without the service. */
function requireApi(action: string): never {
  throw new Error(
    `${action} needs the planner backend. Deploy the family-planner service and set VITE_PLANNER_API_URL — this cannot run in the browser alone.`,
  )
}

/**
 * Reads and writes that are purely local state fall back to the browser store
 * so anything the owner enters keeps working offline. Real remote data always
 * wins when the API is configured and reachable.
 */
async function call<T>(real: () => Promise<T>, local: () => T): Promise<T> {
  if (HAS_API) {
    try {
      const result = await real()
      _apiUnreachable = false
      return result
    } catch (error) {
      _apiUnreachable = true
      throw error
    }
  }
  return local()
}

// ─── Local store ─────────────────────────────────────────────

const STORE_KEY = 'nb-planner-v2'

// The v1 store held the seeded demo calendar. Drop it on load so a browser that
// ran the earlier build can't resurrect sample events.
try {
  localStorage.removeItem('nb-planner-mock-v1')
} catch {
  // storage unavailable — nothing to clean up
}

interface LocalState {
  connections: CalendarConnection[]
  calendars: LinkedCalendar[]
  events: PlannerEvent[]
  rules: ScanRule[]
  candidates: EventCandidate[]
  destinations: Destination[]
  deals: TravelDeal[]
}

/**
 * One empty calendar so pasted and manually added events have somewhere to go
 * before any provider account is linked. It holds nothing on creation.
 */
const LOCAL_CALENDAR: LinkedCalendar = {
  id: 'cal-local',
  name: 'My Planner (this browser)',
  provider: 'local',
  accountEmail: 'local only',
  color: '#0ea5e9',
  enabled: true,
  readOnly: false,
  memberId: null,
}

let _memoryState: LocalState | null = null

function emptyState(): LocalState {
  return {
    connections: [],
    calendars: [LOCAL_CALENDAR],
    events: [],
    rules: [],
    candidates: [],
    destinations: [],
    deals: [],
  }
}

function loadState(): LocalState {
  if (_memoryState) return _memoryState
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) {
      _memoryState = { ...emptyState(), ...(JSON.parse(raw) as LocalState) }
      return _memoryState
    }
  } catch {
    // corrupted store — start clean
  }
  _memoryState = emptyState()
  saveState(_memoryState)
  return _memoryState
}

function saveState(state: LocalState) {
  _memoryState = state
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state))
  } catch {
    // storage unavailable — keep in memory only
  }
}

/** Wipes everything stored in this browser. */
export function clearPlannerLocalData(): void {
  saveState(emptyState())
}

// ─── Family members ──────────────────────────────────────────

export async function listMembers(): Promise<FamilyMember[]> {
  return call(
    () => apiGet<FamilyMember[]>('/api/planner/members'),
    () => [...MEMBERS],
  )
}

// ─── Calendar connections ────────────────────────────────────

export async function listConnections(): Promise<CalendarConnection[]> {
  return call(
    () => apiGet<CalendarConnection[]>('/api/planner/connections'),
    () => [...loadState().connections],
  )
}

export async function listCalendars(): Promise<LinkedCalendar[]> {
  return call(
    () => apiGet<LinkedCalendar[]>('/api/planner/calendars'),
    () => [...loadState().calendars],
  )
}

// ─── Direct provider linking (no backend) ────────────────────

const OAUTH_CONNECTION_PREFIX = 'oauth:'

function oauthConnectionId(provider: OAuthProvider): string {
  return `${OAUTH_CONNECTION_PREFIX}${provider}`
}

function providerOf(connectionId: string): OAuthProvider | null {
  if (!connectionId.startsWith(OAUTH_CONNECTION_PREFIX)) return null
  const provider = connectionId.slice(OAUTH_CONNECTION_PREFIX.length)
  return provider === 'google' || provider === 'microsoft' ? provider : null
}

function connectionFor(session: OAuthSession, calendarIds: string[], lastSync: string | null): CalendarConnection {
  return {
    id: oauthConnectionId(session.provider),
    provider: session.provider,
    status: 'connected',
    accountEmail: session.accountEmail,
    scopeSummary:
      session.provider === 'google'
        ? 'Calendar read-only · signed in directly with Google'
        : 'Calendars.Read · signed in directly with Microsoft',
    calendarIds,
    lastSync,
  }
}

/**
 * Pull the account's calendars and their events into the local store. The
 * owner's own choices — which calendars are shown, and who each belongs to —
 * are preserved across syncs; everything else is replaced by what the provider
 * currently reports, so deletions upstream disappear here too.
 */
async function syncProviderIntoStore(provider: OAuthProvider): Promise<CalendarConnection> {
  const session = getSession(provider)
  if (!session) throw new Error(`No ${provider} account is linked.`)

  const state = loadState()
  const previous = new Map(state.calendars.filter((c) => c.provider === provider).map((c) => [c.id, c]))

  const fetched = await fetchProviderCalendars(provider, session.accountEmail)
  const calendars = fetched.map((calendar) => {
    const prior = previous.get(calendar.id)
    return prior ? { ...calendar, enabled: prior.enabled, memberId: prior.memberId } : calendar
  })

  const events = await fetchProviderEvents(provider, calendars)

  state.calendars = [...state.calendars.filter((c) => c.provider !== provider), ...calendars]
  // Provider events are replaced wholesale; anything the owner created stays.
  state.events = [
    ...state.events.filter((e) => !calendars.some((c) => c.id === e.calendarId) && !e.id.startsWith(`${provider}:`)),
    ...events,
  ]

  const connection = connectionFor(
    session,
    calendars.map((c) => c.id),
    new Date().toISOString(),
  )
  state.connections = [...state.connections.filter((c) => c.id !== connection.id), connection]
  saveState(state)
  return connection
}

/**
 * Consent happens on the provider's own domain and no password reaches this
 * app. With a backend deployed the token exchange runs server-side; without one,
 * the browser holds a read-only calendar token directly (Google Identity
 * Services for Google, auth code + PKCE for Microsoft).
 */
export async function connectCalendarProvider(provider: CalendarProvider): Promise<CalendarConnection> {
  if (HAS_API) {
    const { authorizeUrl } = await apiPost<{ authorizeUrl: string }>(
      `/api/planner/connections/${provider}/authorize`,
    )
    window.location.href = authorizeUrl
    return new Promise<CalendarConnection>(() => {}) // navigation takes over
  }

  if (provider !== 'google' && provider !== 'microsoft') {
    requireApi(`Linking a ${provider} calendar`)
  }
  if (!isProviderConfigured(provider)) {
    throw new Error(
      provider === 'google'
        ? 'Google sign-in is not set up for this build. Add VITE_GOOGLE_CLIENT_ID — see docs/family-planner-architecture.md for the 5-minute Google Cloud Console setup.'
        : 'Microsoft sign-in is not set up for this build. Add VITE_MS_CLIENT_ID — see docs/family-planner-architecture.md for the Azure app registration steps.',
    )
  }

  if (provider === 'microsoft') {
    await connectMicrosoft() // full-page redirect; resumed by completePendingOAuth()
  }
  await connectGoogle()
  return syncProviderIntoStore('google')
}

/**
 * Finishes a Microsoft sign-in that navigated away and came back. Call once on
 * mount; returns the connection when one was just completed.
 */
export async function completePendingOAuth(): Promise<CalendarConnection | null> {
  const session = await completeMicrosoftRedirect()
  if (!session) return null
  return syncProviderIntoStore('microsoft')
}

export async function syncConnection(id: string): Promise<CalendarConnection> {
  const provider = providerOf(id)
  if (provider) return syncProviderIntoStore(provider)
  if (!HAS_API) requireApi('Syncing this calendar')
  return apiPost<CalendarConnection>(`/api/planner/connections/${encodeURIComponent(id)}/sync`)
}

export async function disconnectConnection(id: string): Promise<void> {
  const provider = providerOf(id)
  if (provider) {
    await revokeProvider(provider)
    const state = loadState()
    const calendarIds = new Set(state.calendars.filter((c) => c.provider === provider).map((c) => c.id))
    state.calendars = state.calendars.filter((c) => !calendarIds.has(c.id))
    state.events = state.events.filter((e) => !calendarIds.has(e.calendarId))
    state.connections = state.connections.filter((c) => c.id !== id)
    saveState(state)
    return
  }
  return call(
    () => apiPost<void>(`/api/planner/connections/${encodeURIComponent(id)}/disconnect`),
    () => {
      const state = loadState()
      const conn = state.connections.find((c) => c.id === id)
      if (conn) {
        state.calendars = state.calendars.filter((c) => !conn.calendarIds.includes(c.id))
        state.events = state.events.filter((e) => !conn.calendarIds.includes(e.calendarId))
      }
      state.connections = state.connections.filter((c) => c.id !== id)
      saveState(state)
    },
  )
}

export async function setCalendarEnabled(calendarId: string, enabled: boolean): Promise<void> {
  const state = loadState()
  const cal = state.calendars.find((c) => c.id === calendarId)
  if (cal) cal.enabled = enabled
  if (!enabled && cal) {
    // Drop the cached events so a hidden calendar isn't quietly kept around.
    state.events = state.events.filter((e) => e.calendarId !== calendarId)
  }
  saveState(state)

  if (HAS_API) {
    await apiPut<void>(`/api/planner/calendars/${encodeURIComponent(calendarId)}`, { enabled })
    return
  }
  // Newly shown provider calendar: fetch what it holds right away.
  if (enabled && cal && (cal.provider === 'google' || cal.provider === 'microsoft')) {
    const fresh = await fetchProviderEvents(cal.provider, [cal])
    const next = loadState()
    next.events = [...next.events.filter((e) => e.calendarId !== calendarId), ...fresh]
    saveState(next)
  }
}

/**
 * Attribute a calendar to a household member so its events feed the per-person
 * filters, conflict detection, and free-window search.
 */
export async function setCalendarMember(calendarId: string, memberId: string | null): Promise<void> {
  const state = loadState()
  const cal = state.calendars.find((c) => c.id === calendarId)
  if (!cal) return
  cal.memberId = memberId
  state.events = state.events.map((e) =>
    e.calendarId === calendarId ? { ...e, memberIds: memberId ? [memberId] : [] } : e,
  )
  saveState(state)
  if (HAS_API) {
    await apiPut<void>(`/api/planner/calendars/${encodeURIComponent(calendarId)}`, { memberId })
  }
}

// ─── Events ──────────────────────────────────────────────────

export async function listEvents(): Promise<PlannerEvent[]> {
  return call(
    () => apiGet<PlannerEvent[]>('/api/planner/events'),
    () => [...loadState().events],
  )
}

export async function createEvent(
  draft: ParsedEventDraft & { calendarId: string; origin?: PlannerEvent['origin'] },
): Promise<PlannerEvent> {
  return call(
    () => apiPost<PlannerEvent>('/api/planner/events', draft),
    () => {
      const state = loadState()
      const event: PlannerEvent = {
        id: `evt-${Date.now()}`,
        title: draft.title,
        start: draft.start,
        end: draft.end,
        allDay: draft.allDay,
        location: draft.location,
        notes: draft.notes,
        calendarId: draft.calendarId,
        origin: draft.origin ?? 'manual',
        attendees: draft.attendees,
        memberIds: draft.memberIds,
        category: draft.category,
        updatedAt: new Date().toISOString(),
      }
      state.events.push(event)
      saveState(state)
      return event
    },
  )
}

export async function deleteEvent(id: string): Promise<void> {
  return call(
    () => apiDelete<void>(`/api/planner/events/${encodeURIComponent(id)}`),
    () => {
      const state = loadState()
      state.events = state.events.filter((e) => e.id !== id)
      saveState(state)
    },
  )
}

// ─── Scan rules (Gmail queries / WhatsApp threads) ───────────

export async function listScanRules(): Promise<ScanRule[]> {
  return call(
    () => apiGet<ScanRule[]>('/api/planner/scan-rules'),
    () => [...loadState().rules],
  )
}

export async function saveScanRule(rule: ScanRule): Promise<ScanRule> {
  return call(
    () => apiPut<ScanRule>(`/api/planner/scan-rules/${encodeURIComponent(rule.id)}`, rule),
    () => {
      const state = loadState()
      const idx = state.rules.findIndex((r) => r.id === rule.id)
      if (idx >= 0) state.rules[idx] = rule
      else state.rules.push(rule)
      saveState(state)
      return rule
    },
  )
}

export async function deleteScanRule(id: string): Promise<void> {
  return call(
    () => apiDelete<void>(`/api/planner/scan-rules/${encodeURIComponent(id)}`),
    () => {
      const state = loadState()
      state.rules = state.rules.filter((r) => r.id !== id)
      saveState(state)
    },
  )
}

/**
 * Reads the configured Gmail searches and WhatsApp sources. This is inherently
 * server-side work — the provider tokens live in the backend — so there is no
 * offline equivalent. Use the Inbox paste box in the meantime.
 */
export async function runScan(ruleId?: string): Promise<EventCandidate[]> {
  if (!HAS_API) requireApi('Scanning Gmail and WhatsApp sources')
  return apiPost<EventCandidate[]>('/api/planner/scan', ruleId ? { ruleId } : {})
}

// ─── Review inbox ────────────────────────────────────────────

export async function listCandidates(): Promise<EventCandidate[]> {
  return call(
    () => apiGet<EventCandidate[]>('/api/planner/candidates'),
    () => [...loadState().candidates],
  )
}

/** Parse an arbitrary pasted/forwarded message into a reviewable candidate. */
export async function ingestText(input: {
  text: string
  channel: IngestChannel
  from?: string
  context?: string
  targetCalendarId: string
  memberIds: string[]
}): Promise<EventCandidate> {
  const parsed = parseEventText(input.text, { memberIds: input.memberIds, category: 'other' })
  if (!parsed) throw new Error('No date or time found in that message — add one and try again.')
  return call(
    () => apiPost<EventCandidate>('/api/planner/ingest', input),
    () => {
      const state = loadState()
      const candidate: EventCandidate = {
        id: `cand-${Date.now()}`,
        channel: input.channel,
        receivedAt: new Date().toISOString(),
        from: input.from || 'Pasted by you',
        context: input.context || (input.channel === 'whatsapp' ? 'Pasted WhatsApp message' : 'Pasted message'),
        excerpt: input.text,
        confidence: parsed.confidence,
        parsed: parsed.draft,
        status: 'pending',
        targetCalendarId: input.targetCalendarId,
      }
      state.candidates.unshift(candidate)
      saveState(state)
      return candidate
    },
  )
}

/** Accepting is the only path from the inbox onto the calendar. */
export async function acceptCandidate(
  id: string,
  overrides?: Partial<ParsedEventDraft> & { targetCalendarId?: string },
): Promise<PlannerEvent> {
  return call(
    () => apiPost<PlannerEvent>(`/api/planner/candidates/${encodeURIComponent(id)}/accept`, overrides ?? {}),
    () => {
      const state = loadState()
      const candidate = state.candidates.find((c) => c.id === id)
      if (!candidate) throw new Error('Candidate not found')
      const draft = { ...candidate.parsed, ...overrides }
      const event: PlannerEvent = {
        id: `evt-${candidate.channel}-${Date.now()}`,
        title: draft.title,
        start: draft.start,
        end: draft.end,
        allDay: draft.allDay,
        location: draft.location,
        notes: draft.notes,
        calendarId: overrides?.targetCalendarId ?? candidate.targetCalendarId,
        origin: candidate.channel,
        originRef: candidate.id,
        organizer: candidate.from,
        attendees: draft.attendees,
        memberIds: draft.memberIds,
        category: draft.category,
        updatedAt: new Date().toISOString(),
      }
      state.events.push(event)
      candidate.status = 'accepted'
      saveState(state)
      return event
    },
  )
}

export async function rejectCandidate(id: string): Promise<void> {
  return call(
    () => apiPost<void>(`/api/planner/candidates/${encodeURIComponent(id)}/reject`),
    () => {
      const state = loadState()
      const candidate = state.candidates.find((c) => c.id === id)
      if (candidate) candidate.status = 'rejected'
      saveState(state)
    },
  )
}

// ─── Phase 2: destinations & deals ───────────────────────────

export async function listDestinations(): Promise<Destination[]> {
  return call(
    () => apiGet<Destination[]>('/api/planner/destinations'),
    () => [...loadState().destinations],
  )
}

export async function saveDestination(destination: Destination): Promise<Destination> {
  return call(
    () => apiPut<Destination>(`/api/planner/destinations/${encodeURIComponent(destination.id)}`, destination),
    () => {
      const state = loadState()
      const idx = state.destinations.findIndex((d) => d.id === destination.id)
      if (idx >= 0) state.destinations[idx] = destination
      else state.destinations.push(destination)
      saveState(state)
      return destination
    },
  )
}

export async function deleteDestination(id: string): Promise<void> {
  return call(
    () => apiDelete<void>(`/api/planner/destinations/${encodeURIComponent(id)}`),
    () => {
      const state = loadState()
      state.destinations = state.destinations.filter((d) => d.id !== id)
      state.deals = state.deals.filter((d) => d.destinationId !== id)
      saveState(state)
    },
  )
}

export async function listDeals(): Promise<TravelDeal[]> {
  return call(
    () => apiGet<TravelDeal[]>('/api/planner/deals'),
    () => [...loadState().deals],
  )
}

/**
 * Prices every enabled destination against the flight and cruise sources.
 * Those are server-side API calls with keys attached, so this has no offline
 * equivalent — an offline planner shows no deals rather than invented ones.
 */
export async function refreshDeals(): Promise<TravelDeal[]> {
  if (!HAS_API) requireApi('Checking flight and cruise prices')
  return apiPost<TravelDeal[]>('/api/planner/deals/refresh')
}

// ─── Assistant ───────────────────────────────────────────────

/**
 * With the backend configured, the question goes to Claude with the calendar
 * assembled server-side. Offline, the deterministic rules engine answers over
 * the same events the UI is showing — it reports on real entries only, and has
 * nothing to say when the planner is empty.
 */
export async function askAssistant(question: string, ctx: AssistantContext): Promise<AssistantMessage> {
  return call(
    () => apiPost<AssistantMessage>('/api/planner/assistant', { question }),
    () => {
      const answer = answerLocally(question, ctx)
      return {
        id: `msg-${Date.now()}`,
        role: 'assistant' as const,
        text: answer.text,
        createdAt: new Date().toISOString(),
        citations: answer.citations,
        suggestions: answer.suggestions,
        isOffline: true,
      }
    },
  )
}
